import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Zap, Pencil, Sparkles, Clock, Boxes, CalendarClock, ClipboardCheck, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import RuleForm, { OP_LABELS } from "@/components/RuleForm";

const GROUP_ICON = {
  "Ocorrências": AlertTriangle,
  "Checklists": ClipboardCheck,
  "Controle de ponto": Clock,
  "Estoque": Boxes,
  "Validade": CalendarClock,
};

const EMPTY = { name: "", description: "", trigger: "occurrence.created", conditions: [], actions: [], is_active: true };

function describeCondition(c, trigger) {
  const field = trigger?.fields?.find((f) => f.key === c.field);
  let value = c.value;
  if (field?.type === "enum") value = field.options.find((o) => o.value === c.value)?.label || c.value;
  if (field?.type === "boolean") value = c.value === "true" ? "Sim" : "Não";
  return `${field?.label || c.field} ${OP_LABELS[c.op] || c.op} ${value}`;
}

export default function Rules() {
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      const [{ data: rules }, { data: cat }] = await Promise.all([api.get("/rules"), api.get("/rules/catalog")]);
      setItems(rules);
      setCatalog(cat);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name || "",
      description: r.description || "",
      trigger: r.trigger || "occurrence.created",
      conditions: r.conditions || [],
      actions: r.actions || [],
      is_active: r.is_active !== false,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim() || form.actions.length === 0) {
      toast.error("Nome e pelo menos uma ação são obrigatórios");
      return;
    }
    try {
      if (editing) {
        await api.patch(`/rules/${editing.id}`, form);
        toast.success("Regra atualizada");
      } else {
        await api.post("/rules", form);
        toast.success("Regra criada");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar a regra");
    }
  };

  const activatePreset = async (p) => {
    try {
      await api.post(`/rules/presets/${p.id}`);
      toast.success(`Regra "${p.name}" ativada`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao ativar modelo");
    }
  };

  const toggleActive = async (r) => {
    try {
      await api.patch(`/rules/${r.id}`, { is_active: !r.is_active });
      load();
    } catch (err) {
      toast.error("Falha ao alterar a regra");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover regra?")) return;
    try {
      await api.delete(`/rules/${id}`);
      toast.success("Regra removida");
      load();
    } catch (err) {
      toast.error("Falha ao remover");
    }
  };

  const triggerOf = (r) => catalog?.triggers?.find((t) => t.value === r.trigger);
  const existingNames = new Set(items.map((r) => r.name));

  return (
    <Layout
      title="Motor de Regras"
      subtitle="Automatize alertas e escalonamentos: ponto, estoque, validade, checklists e ocorrências"
      actions={
        <Button className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="rule-new" onClick={openNew}>
          <Plus className="h-4 w-4 mr-2" /> Nova regra
        </Button>
      }
    >
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? `Editar regra — ${editing.name}` : "Nova regra"}</DialogTitle>
          </DialogHeader>
          <RuleForm form={form} setForm={setForm} catalog={catalog} />
          <DialogFooter>
            <Button onClick={submit} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="rule-submit">Salvar regra</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {catalog?.presets?.length > 0 && (
        <Card className="p-5 mb-6 rounded-xl border-emerald-200 bg-emerald-50/50" data-testid="rule-presets">
          <div className="flex items-center gap-2 font-display font-bold tracking-tight text-emerald-900">
            <Sparkles className="h-4 w-4" /> Modelos prontos — ative com um clique
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {catalog.presets.map((p) => {
              const active = existingNames.has(p.name);
              const Icon = GROUP_ICON[catalog.triggers.find((t) => t.value === p.trigger)?.group] || Zap;
              return (
                <Button key={p.id} size="sm" variant="outline" disabled={active} onClick={() => activatePreset(p)} className="bg-white border-emerald-200 text-emerald-900 hover:bg-emerald-100 font-body" data-testid={`rule-preset-${p.id}`}>
                  <Icon className="h-3.5 w-3.5 mr-1.5" /> {p.name}{active ? " ✓" : ""}
                </Button>
              );
            })}
          </div>
        </Card>
      )}

      <div data-testid="rules-page" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && (
          <Card className="sm:col-span-2 lg:col-span-3 p-10 text-center text-stone-500 font-body rounded-xl border-stone-200 bg-white">
            <Zap className="h-6 w-6 mx-auto mb-2 text-stone-400" />
            Nenhuma regra criada. Ative um modelo pronto acima ou crie a sua.
          </Card>
        )}
        {items.map((r) => {
          const trig = triggerOf(r);
          const Icon = GROUP_ICON[trig?.group] || Zap;
          return (
            <Card key={r.id} className="p-5 rounded-xl border-stone-200 bg-white" data-testid={`rule-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <button type="button" onClick={() => openEdit(r)} className="font-display font-bold tracking-tight text-emerald-900 hover:underline inline-flex items-center gap-1.5 text-left" data-testid={`rule-edit-${r.id}`}>
                    {r.name}
                    <Pencil className="h-3 w-3 opacity-40" />
                  </button>
                  <div className="mt-1 flex flex-wrap gap-1">
                    <Badge variant="outline" className="border-emerald-200 text-emerald-800 text-[10px] inline-flex items-center gap-1">
                      <Icon className="h-3 w-3" /> {trig?.label || r.trigger}
                    </Badge>
                  </div>
                </div>
                <Switch checked={r.is_active} onCheckedChange={() => toggleActive(r)} data-testid={`rule-toggle-${r.id}`} />
              </div>
              <div className="mt-3 text-xs font-body text-stone-600 space-y-1">
                <div><span className="font-bold text-stone-500">SE</span> {r.conditions?.length ? r.conditions.map((c) => describeCondition(c, trig)).join(" e ") : "qualquer evento"}</div>
                <div><span className="font-bold text-stone-500">ENTÃO</span> {r.actions?.map((a) => catalog?.actions?.find((x) => x.value === a.type)?.label || a.type).join(" · ")}</div>
              </div>
              <div className="mt-3 text-xs font-body text-stone-400">
                Disparada <strong>{r.triggered_count || 0}</strong>x · última: {r.last_triggered_at ? formatDateTime(r.last_triggered_at) : "nunca"}
              </div>
              <div className="mt-2 flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => remove(r.id)} className="text-rose-600 hover:text-rose-700" data-testid={`rule-remove-${r.id}`}>
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Remover
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </Layout>
  );
}
